import {
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Modal, Switch, Tooltip } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../../api";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../utils/Utils";
import "./VoteModal.scss";
import { conversationType } from "../../../config/Constant";
import { useNavigate } from "react-router-dom";

const { TextArea } = Input;

const CreateVoteModal = ({ isOpen, onClose, friendId }) => {
  const navigate = useNavigate();
  const { selectedConversation } = useMenuContext();
  const { user, languageMap } = useInfoUser();
  const [form] = Form.useForm();
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const defaultValues = {
    options: [
      { voteOptionId: uuidv4(), optionText: "" },
      { voteOptionId: uuidv4(), optionText: "" },
    ],
    deadline: null,
    pinned: false,
    multipleChoice: false,
    allowAddOptions: false,
    hideResults: false,
    hideVoters: false,
  };

  const [initialValues, setInitialValues] = useState(defaultValues);

  const resetForm = () => {
    const resetVals = {
      ...defaultValues,
      options: [
        { voteOptionId: uuidv4(), optionText: "" },
        { voteOptionId: uuidv4(), optionText: "" },
      ],
    };
    setInitialValues(resetVals);
    form.resetFields();
    form.setFieldsValue(resetVals);
    setShowAdvancedSettings(false);
  };

  const onFinish = async (values) => {
    try {
      let createdConversation = null;

      if (friendId) {
        const userIds =
          user?.userId === friendId ? [user?.userId] : [user?.userId, friendId];

        createdConversation = await apiFactory.conversationApi.createGroup({
          name: "",
          userIds: userIds,
          conversationType: conversationType.PERSONAL,
        });
      }

      const req = {
        ...values,
        conversationId: friendId
          ? createdConversation?.data?.conversationId
          : selectedConversation?.conversationId,
      };

      const rs = await apiFactory.messageVoteApi.createVoteMessage(req);

      if (rs?.status !== 200) {
        openNotificationError("An error occurred while creating the poll.");
        resetForm();
        onClose();
        return;
      }

      openNotificationSuccess("Poll created successfully!");
      navigate(
        `/conversation/chat/${createdConversation?.data?.conversationId}`,
      );
    } catch (error) {
      openNotificationError("An error occurred while creating the poll.");
    } finally {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal
      title={languageMap?.["modal.message.vote.title"] ?? "Create poll"}
      open={isOpen}
      onCancel={onClose}
      footer={false}
      closeIcon
      closable
      width={showAdvancedSettings ? 800 : 450}
      className="vote-modal"
    >
      <Form
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        layout="vertical"
        initialValues={initialValues}
        className="vote-form"
      >
        <div className="vote-form-content flex">
          {/* LEFT SIDE */}
          <div className="main-content flex-1">
            <Form.Item
              label={
                languageMap?.["modal.message.vote.topicLabel"] ?? "Topic name"
              }
              name="voteTitle"
              rules={[{ required: true, message: "Enter your question!" }]}
            >
              <TextArea
                placeholder={
                  languageMap?.["modal.message.vote.topicPlaceholder"] ??
                  "Enter your question"
                }
                maxLength={200}
                showCount
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>

            <Form.Item
              label={
                languageMap?.["modal.message.vote.optionsLabel"] ?? "Options"
              }
            >
              <Form.List name="options">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <div key={key} className="flex items-center gap-2 mb-2">
                        <Form.Item
                          {...restField}
                          name={[name, "optionText"]}
                          rules={[
                            {
                              required: true,
                              message: "Please enter an option!",
                            },
                            {
                              validator: (_, value) => {
                                if (!value || value.trim() === "")
                                  return Promise.reject(
                                    new Error("Options cannot be blank!"),
                                  );
                                return Promise.resolve();
                              },
                            },
                          ]}
                          className="flex-1 mb-0"
                        >
                          <Input
                            placeholder={`${
                              languageMap?.["modal.message.vote.option"] ??
                              "Option"
                            } ${index + 1}`}
                          />
                        </Form.Item>
                        {fields.length > 2 && (
                          <MinusCircleOutlined
                            onClick={() => remove(name)}
                            className="text-gray-500 hover:text-red-500 cursor-pointer text-lg"
                          />
                        )}
                      </div>
                    ))}
                    <Button
                      type="link"
                      onClick={() =>
                        add({ voteOptionId: uuidv4(), optionText: "" })
                      }
                      block
                      icon={<PlusOutlined />}
                      className="add-option-button"
                    >
                      {languageMap?.["modal.message.vote.addOptionBtn"] ??
                        "Add an option"}
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>
          </div>

          {/* RIGHT SIDE (ADVANCED SETTINGS) */}
          {showAdvancedSettings && (
            <div className="advanced-settings ml-8 pl-8 flex-4 border-l border-solid border-gray-200">
              <Form.Item
                label={languageMap?.["modal.message.vote.votingDeadline"]}
                name="deadline"
              >
                <DatePicker
                  showTime
                  placeholder={languageMap?.["modal.message.vote.noDeadline"]}
                  className="w-full"
                  format="YYYY-MM-DD HH:mm:ss"
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  disabledTime={(current) => {
                    if (dayjs(current).isSame(dayjs(), "day")) {
                      return {
                        disabledHours: () => {
                          const hours = [];
                          for (let i = 0; i < dayjs().hour(); i++) {
                            hours.push(i);
                          }
                          return hours;
                        },
                        disabledMinutes: () => {
                          const minutes = [];
                          for (let i = 0; i < dayjs().minute(); i++) {
                            minutes.push(i);
                          }
                          return minutes;
                        },
                        disabledSeconds: () => {
                          const seconds = [];
                          for (let i = 0; i < dayjs().second(); i++) {
                            seconds.push(i);
                          }
                          return seconds;
                        },
                      };
                    }
                    return {};
                  }}
                />
              </Form.Item>

              <h4 className="text-sm font-semibold mb-2">
                {languageMap?.["modal.message.vote.advancedSettings"]}
              </h4>

              <Form.Item
                label={
                  <div className="flex items-center">
                    {languageMap?.["modal.message.vote.selectMultipleOptions"]}
                    <Tooltip title="Allow users to select more than one option">
                      <QuestionCircleOutlined className="ml-1 text-gray-400" />
                    </Tooltip>
                  </div>
                }
                name="multipleChoice"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label={
                  <div className="flex items-center">
                    {languageMap?.["modal.message.vote.canAddOptions"]}
                    <Tooltip title="Allow users to add their own options">
                      <QuestionCircleOutlined className="ml-1 text-gray-400" />
                    </Tooltip>
                  </div>
                }
                name="allowAddOptions"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <h4 className="text-sm font-semibold mt-4 mb-2">
                {languageMap?.["modal.message.vote.anonymousVoting"]}
              </h4>

              <Form.Item
                label={
                  <div className="flex items-center">
                    {
                      languageMap?.[
                        "modal.message.vote.hideResultsUntilAVoteIsCast"
                      ]
                    }
                    <Tooltip title="Poll results will only be shown after voting">
                      <QuestionCircleOutlined className="ml-1 text-gray-400" />
                    </Tooltip>
                  </div>
                }
                name="hideResults"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label={
                  <div className="flex items-center">
                    {languageMap?.["modal.message.vote.hideVoters"]}
                    <Tooltip title="Do not display the names of voters">
                      <QuestionCircleOutlined className="ml-1 text-gray-400" />
                    </Tooltip>
                  </div>
                }
                name="hideVoters"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex justify-between items-center mt-6">
          <Button
            type="text"
            icon={
              <SettingOutlined
                className="text-xl"
                style={{ color: showAdvancedSettings ? "#1677ff" : "inherit" }}
              />
            }
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            className={showAdvancedSettings ? "bg-[#E5F1FF]" : ""}
          />
          <div className="flex gap-[10px]">
            <Button
              className="!bg-[#f0f0f0] !text-black hover:!bg-[#e0e0e0] font-medium"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              {languageMap?.["project.management.config.CancelBtn"] ?? "Cancel"}
            </Button>
            <Button className="font-medium" type="primary" htmlType="submit">
              {languageMap?.["project.management.config.createBtn"] ??
                "Create poll"}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateVoteModal;
